// ========================================================================
// 📁 index.js
// Purpose: The main component, now acting as an orchestrator. It fetches
// data, uses the logic hook, and passes props down to layout components.
// ========================================================================

import {Clock, Calendar, Tag, X, Copy, User, MapPin, Building, DraftingCompass} from "lucide-react";
import {useTaskActions} from "../Recee/useTaskActions";
import confirmToast from "./ConfirmBrandToast";
import toast from "react-hot-toast";
import {JobDetail} from "../Recee/JobDetail";
import {BRANDS, formatTimestamp, getStatusConfig, PROGRAMMES} from "../Recee/constants";
import {InfoCard, InfoRow} from "../Recee/InfoCard";
import QRCode from "react-qr-code";
import { Button } from "@blueprintjs/core";
import React from "react";

const TaskPopUp = ({ taskData, onClose, share, getTaskData, goToPreviousTask, goToNextTask }) => {
  const { approveOrDenyJob, updateTaskDetails, updateReceeBrand } = useTaskActions(taskData?._id, getTaskData);

  const handleBrandOrProgramUpdate = (brand, channel_programme) => {
    confirmToast(brand, channel_programme, () => updateTaskDetails({ approvedBrand: brand, channel_programme }));
  };

  const ShareLink = `http://kopilot.live/sharedTask/${taskData._id}`

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(ShareLink);
      toast.success("Task Link copied to clipboard!");
    } catch (err) {
      toast.error("Could not copy link automatically.");
    }
  };

  if (!taskData) {
    return (
        <div style={{zIndex: 100}} className="fixed inset-0 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="text-white text-2xl">Loading Task...</div>
        </div>
    );
  }

  const statusConfig = getStatusConfig(taskData.status);
  const outlet = taskData.outletWorkDetails || {};
  const vendor = taskData.vendorData || {};

  return (
      <div style={{ zIndex: 100 }} className="fixed inset-0 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <div className="relative w-full max-w-7xl max-h-[95vh] overflow-hidden rounded-2xl bg-gray-50 shadow-2xl flex flex-col">
          {/* HEADER */}
          <header className="relative bg-gradient-to-r from-red-600 via-red-800 to-black px-6 py-4 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-white">Task Details</h1>
                <p className="text-red-100 text-sm">ID: {taskData._id}</p>
              </div>
              {!share && <div className="flex items-center gap-2">
                <button
                    onClick={goToPreviousTask}
                    className="bg-red-400 hover:bg-red-500 text-white px-6 py-3 rounded-lg transition-all duration-200 hover:scale-105"
                >
                  ⬅️ Previous
                </button>
                <button
                    onClick={goToNextTask}
                    className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg transition-all duration-200 hover:scale-105"
                >
                  Next ➡️
                </button>
              </div>}


              {!share && <button onClick={onClose}
                                className="p-2 rounded-full bg-white/20 hover:bg-white/30 text-white"> X </button>}
            </div>
          </header>

          {/* CONTENT */}
          <main className="overflow-y-auto flex-1 p-6 space-y-6">
            {/* Quick Info & Actions */}
            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <InfoCard label="GCC Code" value={outlet.gccNum} icon={<Tag className="w-5 h-5 text-gray-600" />} />
              <InfoCard label="Status" value={taskData.status} config={statusConfig} />

              {/* Brand & Program Selectors */}
              <InfoCard label="Brand" value={taskData.brand || ""} icon={<DraftingCompass className="w-5 h-5 text-gray-600" />} />

              <div className="bg-white p-3 rounded-xl border">
                <label className="text-xs font-medium text-gray-500">Channel Programme</label>
                <select className="mb-5 select select-bordered w-full" value={taskData.channel_programme || ''} onChange={(e) => handleBrandOrProgramUpdate(taskData.brand, e.target.value)}>
                  <option value="" disabled>Select Programme</option>
                  {PROGRAMMES.map(p => <option key={p._id} value={p.name}>{p.name}</option>)}
                </select>
              </div>
            </section>

            {/* Share Section */}

                <section className="bg-green-50 rounded-xl p-4 border border-green-200 flex items-center justify-between gap-4">
                  <div className="flex items-center space-x-3">
                    <QRCode value={ShareLink} size={64} />
                    <div>
                      <h3 className="font-semibold text-green-900">Share Task</h3>
                      <p className="text-sm text-green-700">{ShareLink}</p>
                    </div>
                  </div>
                  <Button onClick={handleShare} className="bg-green-500 hover:bg-green-600 text-white"><Copy className="w-4 h-4 mr-2" />Copy Link</Button>
                </section>

            {/* Details Grid */}
            <section className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="p-3 bg-blue-100 rounded-full">
                    <Building className="w-6 h-6 text-blue-600"/>
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">Outlet Details</h2>
                </div>
                <div className="space-y-4">
                  <InfoRow
                      icon={<Building className="w-4 h-4 text-gray-500"/>}
                      label="Outlet Name"
                      value={taskData?.outletWorkDetails?.outletName}
                  />
                  <InfoRow
                      icon={<MapPin className="w-4 h-4 text-gray-500"/>}
                      label="Address"
                      value={taskData?.outletWorkDetails?.address}
                  />
                  <InfoRow
                      icon={<MapPin className="w-4 h-4 text-gray-500"/>}
                      label="Town"
                      value={taskData?.outletWorkDetails?.town}
                  />
                  <InfoRow
                      icon={<Tag className="w-4 h-4 text-gray-500"/>}
                      label="Pin Code"
                      value={taskData?.outletWorkDetails?.pinCode}
                  />
                </div>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="p-3 bg-purple-100 rounded-full">
                    <User className="w-6 h-6 text-purple-600"/>
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">Vendor Details</h2>
                </div>
                <div className="space-y-4">
                  <InfoRow
                      icon={<User className="w-4 h-4 text-gray-500"/>}
                      label="Vendor Name"
                      value={taskData?.vendorData?.userName}
                  />
                  <InfoRow
                      icon={<Tag className="w-4 h-4 text-gray-500"/>}
                      label="Email"
                      value={taskData?.vendorData?.email}
                  />

                  <hr/>
                  <InfoRow
                      icon={<Tag className="w-4 h-4 text-gray-500"/>}
                      label="Route"
                      value={taskData?.routes}
                  />

                </div>
              </div>
            </section>

            {/* Timeline */}
            <section className="bg-white rounded-xl p-6 border">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2"><Calendar/> Timeline</h2>
              <div className="grid grid-cols-2 gap-4">
                <InfoRow icon={<Clock className="w-5 h-5 text-green-600"/>} label="Created At"
                         value={formatTimestamp(taskData.createdAt)}/>
                <InfoRow icon={<Clock className="w-5 h-5 text-blue-600"/>} label="Last Updated"
                         value={formatTimestamp(taskData.updatedAt)}/>
              </div>
            </section>

            {/* Job Details Section */}
            <section className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-900">Job Details</h2>
              {taskData.jobs?.map((job, index) => (
                  <JobDetail
                      key={job._id}
                      job={job}
                      index={index}
                      share={share}
                      taskData={taskData}
                      onAction={approveOrDenyJob}
                      updateReceeBrand={updateReceeBrand}
                  />
              ))}
            </section>
          </main>
        </div>
      </div>
  );
};

export default TaskPopUp;
